import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileText, Paperclip, Send } from "lucide-react";
import { API } from "@/lib/site";

const EXPERIENCE = ["Fresh Graduate / 0-1 Year", "1 - 3 Years", "3 - 7 Years", "7+ Years Veteran"];
const BOARDS = ["CBSE", "ICSE/ISC", "State Boards", "Cambridge IGCSE", "IB"];
const MODES = ["Home Visits Only", "Online Digital Batches Only", "Both Home & Online"];
const INCOME = [
  "₹15,000 – ₹25,000 (1-2 Batches)",
  "₹25,000 – ₹40,000 (3-4 Batches)",
  "₹40,000+ (Full-time Batches)",
];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-navy focus:ring-2 focus:ring-navy/15";
const labelCls =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500";

export default function CareerForm() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "", experience: "", subjects: "", preferred_mode: "", expected_income: "", bio: "",
  });
  const [boards, setBoards] = useState([]);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleBoard = (b) =>
    setBoards(boards.includes(b) ? boards.filter((x) => x !== b) : [...boards, b]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Resume must be a PDF or DOC file.");
      e.target.value = "";
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Resume must be under 8 MB.");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.location || !form.experience || !form.subjects || !form.preferred_mode || !form.expected_income) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (boards.length === 0) {
      toast.error("Please select at least one board you're familiar with.");
      return;
    }
    if (!file) {
      toast.error("Please attach your resume (PDF or DOC).");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("boards", boards.join(", "));
      fd.append("resume", file);
      await axios.post(`${API}/careers/apply`, fd);
      toast.success("Application sent! We'll reach out shortly.");
      setDone(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail === undefined
          ? "Could not send. Please email your resume to cheshta.msquare@gmail.com."
          : String(err.response.data.detail)
      );
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="career-success-card"
        className="flex h-full flex-col items-center justify-center rounded-3xl border border-teal-200 bg-[#F0FDFA] p-10 text-center"
      >
        <CheckCircle2 className="h-14 w-14 text-teal-600" />
        <h3 className="mt-5 font-serif text-2xl font-bold text-slate-900">
          Application received!
        </h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
          Thank you, {form.name.split(" ")[0]}. Your resume is with our team —
          we'll contact you on <span className="font-semibold">{form.phone}</span> when a matching batch opens up.
        </p>
        <button
          data-testid="career-another-btn"
          onClick={() => {
            setDone(false);
            setFile(null);
            setForm({ name: "", phone: "", email: "", location: "", experience: "", subjects: "", preferred_mode: "", expected_income: "", bio: "" });
          }}
          className="mt-6 rounded-full border border-navy/20 px-5 py-2 text-sm font-semibold text-navy transition hover:bg-navy hover:text-white"
        >
          Submit another application
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="career-form" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="car-name">Full Name *</label>
          <input id="car-name" data-testid="career-name-input" className={inputCls} placeholder="e.g., Priya Verma" value={form.name} onChange={set("name")} required maxLength={120} />
        </div>
        <div>
          <label className={labelCls} htmlFor="car-phone">Mobile / WhatsApp *</label>
          <input id="car-phone" data-testid="career-phone-input" type="tel" className={inputCls} placeholder="+91 98765 43210" value={form.phone} onChange={set("phone")} required maxLength={20} />
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="car-email">Email Address *</label>
        <input id="car-email" data-testid="career-email-input" type="email" className={inputCls} placeholder="name@example.com" value={form.email} onChange={set("email")} required maxLength={200} />
      </div>
      <div>
        <label className={labelCls} htmlFor="car-location">Current Location *</label>
        <input id="car-location" data-testid="career-location-input" className={inputCls} placeholder="e.g., Jaipur, Rajasthan" value={form.location} onChange={set("location")} required maxLength={120} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="car-exp">Total Teaching Experience *</label>
          <select id="car-exp" data-testid="career-experience-select" className={inputCls} value={form.experience} onChange={set("experience")} required>
            <option value="" disabled>Select experience</option>
            {EXPERIENCE.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="car-mode">Preferred Tutoring Format *</label>
          <select id="car-mode" data-testid="career-mode-select" className={inputCls} value={form.preferred_mode} onChange={set("preferred_mode")} required>
            <option value="" disabled>Select format</option>
            {MODES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="car-subjects">Primary Subjects Handled *</label>
        <input id="car-subjects" data-testid="career-subjects-input" className={inputCls} placeholder="e.g., Class 11-12 Mathematics, JEE Physics" value={form.subjects} onChange={set("subjects")} required maxLength={200} />
      </div>
      <div>
        <span className={labelCls}>Boards Familiar With *</span>
        <div className="flex flex-wrap gap-2">
          {BOARDS.map((b) => (
            <button
              type="button"
              key={b}
              data-testid={`career-board-chip-${b.toLowerCase().replace(/[^a-z]/g, "")}`}
              onClick={() => toggleBoard(b)}
              className={`rounded-full border px-4 py-2 text-xs font-semibold transition ${
                boards.includes(b)
                  ? "border-navy bg-navy text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:border-navy/40"
              }`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="car-income">Target Monthly Income *</label>
        <select id="car-income" data-testid="career-income-select" className={inputCls} value={form.expected_income} onChange={set("expected_income")} required>
          <option value="" disabled>Select expectation</option>
          {INCOME.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div>
        <span className={labelCls}>Upload Resume / CV (PDF or DOC) *</span>
        <label
          htmlFor="car-resume"
          data-testid="career-resume-dropzone"
          className="flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/60 px-4 py-5 transition hover:border-navy/50 hover:bg-white"
        >
          <Paperclip className="h-5 w-5 shrink-0 text-navy" />
          <span className="flex-1 truncate text-sm text-slate-600">
            {file ? (
              <span className="flex items-center gap-2 font-semibold text-navy-dark">
                <FileText className="h-4 w-4" /> {file.name} ({Math.round(file.size / 1024)} KB)
              </span>
            ) : (
              "Click to attach your resume — PDF, DOC or DOCX (max 8 MB)"
            )}
          </span>
        </label>
        <input id="car-resume" data-testid="career-resume-input" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={onFile} required />
      </div>
      <div>
        <label className={labelCls} htmlFor="car-bio">Brief Teaching Philosophy / Note</label>
        <textarea id="car-bio" data-testid="career-bio-input" className={`${inputCls} min-h-[90px] resize-y`} placeholder="Tell us about your teaching style, past student board ranks, and availability." value={form.bio} onChange={set("bio")} maxLength={3000} />
      </div>
      <button
        type="submit"
        data-testid="career-submit-btn"
        disabled={sending}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-navy-dark disabled:cursor-not-allowed disabled:opacity-60"
      >
        {sending ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
        ) : (
          <>
            <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            Apply With Your Resume
          </>
        )}
      </button>
      <p className="text-center text-xs text-slate-400">
        Your application & resume go straight to our inbox. We respond within 48 hours.
      </p>
    </form>
  );
}
